import type { FC } from 'react';
import React from 'react';

import { COLUMN_WIDTH_WITH_3_STEPS, COLUMN_WIDTH_WITH_4_STEPS } from '../config';
import type { TActionStepsComponentDesktop } from '../models';

const getColumnsWidth = (numberOfSteps: number) =>
    numberOfSteps === 3 ? COLUMN_WIDTH_WITH_3_STEPS : COLUMN_WIDTH_WITH_4_STEPS;

export const ActionSteps: FC<TActionStepsComponentDesktop> = ({
    Aligner,
    Block,
    Button,
    Grid,
    Step,
    Text,
    analytics,
    button,
    children,
    createTree,
    dataTestId = 'action-steps',
    heading,
    headingAlign = 'center',
    headingOffset = true,
    sendEvent,
    steps,
    styles,
    subHeading,
    theme = 'grey',
    useNamedContainers,
    withSeparator = true,
    ...rest
}) => {
    const sendAnalytics = () => {
        sendEvent(analytics);
    };

    const {
        button: buttonSlot,
        header: headerSlot,
        step: stepSlot,
        subHeading: subHeadingSlot,
    } = useNamedContainers(children);

    return (
        <Block dataTestId={dataTestId} paddedType="normal" theme={theme} {...rest}>
            {headerSlot ?? (
                <Aligner
                    align={headingAlign}
                    styler={
                        headingOffset
                            ? {
                                  marginBottom: 32,
                              }
                            : undefined
                    }
                >
                    {heading && (
                        <Text styler={{ margin: 0 }} view="medium">
                            {heading}
                        </Text>
                    )}
                    {subHeading && (
                        <Text styler={{ marginBottom: 12, marginTop: 12 }} view="primary-large">
                            {subHeading}
                        </Text>
                    )}
                    {subHeadingSlot}
                </Aligner>
            )}
            {stepSlot ?? (
                <Grid colAutoWidth columnsWidth={getColumnsWidth(steps.length)} justify="between">
                    {steps.map((item, i) => (
                        <Step
                            key={item.id}
                            createTree={createTree}
                            isNotLastItem={i < steps.length - 1}
                            step={item}
                            withSeparator={withSeparator}
                        />
                    ))}
                </Grid>
            )}
            {buttonSlot ??
                (button?.text && (
                    <Aligner
                        align="center"
                        styler={{
                            marginTop: 32,
                        }}
                    >
                        <Button
                            href={button.href}
                            id={`${dataTestId}-button`}
                            onClick={sendAnalytics}
                            size="s"
                            view={button.kind}
                        >
                            {button.text}
                        </Button>
                    </Aligner>
                ))}
        </Block>
    );
};
